import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useMessages } from '@/hooks/useConsoleData';
import { Section } from '@/components/console/Section';
import { sendGrowthOutreach, type OutreachRow } from '@/lib/admin-data';
import { ago, num, when } from '@/lib/format';
import s from './console.module.css';

/**
 * Messages: every personal note that went out (or is waiting, or failed),
 * and the journey emails and pushes the product sent by itself, per person.
 * A failed note can be sent again from here; a queued one sends itself.
 */
type Filter = 'all' | 'queued' | 'failed' | 'sent';

export default function MessagesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const messages = useMessages();
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<Record<string, string>>({});
  const m = messages.data;

  const outreach = useMemo(() => {
    if (!m) return [];
    const needle = q.trim().toLowerCase();
    return m.outreach.filter((r) => (filter === 'all' || r.status === filter) && (!needle || `${r.name} ${r.email} ${r.body} ${r.subject ?? ''}`.toLowerCase().includes(needle)));
  }, [m, filter, q]);

  const journeyByPerson = useMemo(() => {
    const out = new Map<string, { name: string; email: string; steps: { step: string; channel: string; sentAt: string }[] }>();
    for (const j of m?.journey ?? []) {
      const cur = out.get(j.userId) ?? { name: j.name, email: j.email, steps: [] };
      cur.steps.push({ step: j.step, channel: j.channel, sentAt: j.sentAt });
      out.set(j.userId, cur);
    }
    return out;
  }, [m]);

  const resend = async (r: OutreachRow) => {
    setBusy(r.id);
    try {
      const res = await sendGrowthOutreach({ userId: r.userId, channel: r.channel as 'email' | 'sms', subject: r.subject ?? undefined, body: r.body });
      setResult((prev) => ({ ...prev, [r.id]: res.status === 'sent' ? 'Sent again.' : res.status === 'queued' ? (res.message ?? 'Queued.') : `Not sent: ${res.error ?? 'unknown error'}` }));
      void qc.invalidateQueries({ queryKey: ['messages'] });
      void qc.invalidateQueries({ queryKey: ['growth'] });
    } catch (err) {
      setResult((prev) => ({ ...prev, [r.id]: `Not sent: ${(err as Error).message}` }));
    } finally {
      setBusy(null);
    }
  };
  const journal = (userId: string) => navigate(`/people?user=${encodeURIComponent(userId)}&journal=1`);
  const count = (st: Filter) => (m ? (st === 'all' ? m.outreach.length : m.outreach.filter((r) => r.status === st).length) : 0);

  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <h1 className={s.pageTitle}>Messages</h1>
        <span className={s.pageSub}>{m ? `refreshed ${when(m.generatedAt)}` : ''}</span>
      </div>

      {messages.isError && <div className={s.error}>Could not load messages. This page needs GET /provider/admin/messages.</div>}
      {m && (
        <div className={s.strip}>
          <div className={s.stat}><span className={s.statLabel}>Waiting to send</span><span className={s.statVal}>{num(m.counts.queued)}</span><span className={s.statSub}>queued until GHL answers; sends itself</span></div>
          <div className={s.stat}><span className={s.statLabel}>Sent, 7 days</span><span className={s.statVal}>{num(m.counts.sent7d)}</span><span className={s.statSub}>personal notes</span></div>
          <div className={s.stat}><span className={s.statLabel}>Failed, 7 days</span><span className={s.statVal}>{num(m.counts.failed7d)}</span><span className={s.statSub}>{m.counts.failed7d > 0 ? 'send again below' : 'nothing to redo'}</span></div>
          <div className={s.stat}><span className={s.statLabel}>Journey steps</span><span className={s.statVal}>{num(m.journey.length)}</span><span className={s.statSub}>most recent {m.journey.length} the product sent</span></div>
        </div>
      )}

      <div className={s.filters}>
        {(['all', 'queued', 'failed', 'sent'] as Filter[]).map((f) => (
          <button key={f} type="button" className={`${s.filter} ${filter === f ? s.filterOn : ''}`} onClick={() => setFilter(f)}>
            {f === 'all' ? 'All notes' : f[0].toUpperCase() + f.slice(1)} <span className={s.filterCount}>{count(f)}</span>
          </button>
        ))}
        <input className={s.search} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search person or text" aria-label="Search messages" />
      </div>

      <div className={s.list}>
        {m && outreach.length === 0 && <div className={s.empty}>No notes here yet. Notes are written from Today or People.</div>}
        {outreach.map((r) => (
          <details key={r.id} className={s.msg}>
            <summary className={s.msgHead}>
              <span className={`${s.pill} ${s[`pill_${r.status}`] ?? ''}`}>{r.status}</span>
              <strong>{r.name}</strong>
              <span className={s.sub}>{r.email}{r.phone ? ` · ${r.phone}` : ''} · {r.channel.toUpperCase()} · {ago(r.createdAt)}{r.sentBy ? ` · by ${r.sentBy}` : ''}</span>
              {r.subject && <span className={s.sub}>· {r.subject}</span>}
            </summary>
            <div className={s.msgBody}>
              {r.body}
              {r.error && <div className={s.error} style={{ marginTop: 8 }}>{r.error}</div>}
              <div className={s.draftActions} style={{ marginTop: 10 }}>
                {r.status !== 'queued' && <button type="button" className={s.btnGhost} disabled={busy === r.id} onClick={() => void resend(r)}>{busy === r.id ? 'Sending' : `Send again by ${r.channel === 'sms' ? 'SMS' : 'email'}`}</button>}
                {r.ghlUrl && <a className={s.linkA} href={r.ghlUrl} target="_blank" rel="noreferrer">GHL contact</a>}
                <button type="button" className={s.linkBtn} onClick={() => journal(r.userId)}>Journal</button>
                {result[r.id] && <span className={s.sub}>{result[r.id]}</span>}
              </div>
            </div>
          </details>
        ))}
      </div>

      <Section title="Journey emails and pushes" summary="what the product sent by itself, newest person first" count={journeyByPerson.size}>
        {m && journeyByPerson.size === 0 && <div className={s.empty}>No journey steps recorded yet.</div>}
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead><tr><th>Who</th><th>Steps sent</th><th></th></tr></thead>
            <tbody>
              {Array.from(journeyByPerson.entries()).map(([userId, p]) => (
                <tr key={userId}>
                  <td>{p.name}<div className={s.sub}>{p.email}</div></td>
                  <td>{p.steps.map((st) => `${st.step} (${st.channel}, ${when(st.sentAt)})`).join('; ')}</td>
                  <td><button type="button" className={s.linkBtn} onClick={() => journal(userId)}>Journal</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
